const _excluded = ["reportNumber", "reportTypeName"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { createSelector } from 'reselect';
import PropTypes from 'prop-types';
import { get as getValueAtPath } from 'lodash';
import { mergeDeepLeft } from 'ramda';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import moment from 'moment';
import { getValue, appendPath, setValue, pathComparator, enumerateWildcardPath } from '@truqc/content-path';
import { generateMergePatch } from '../../../../behaviors/src/lib/mergePatch';
import entityComposition from '../../common/classes/entityComposition';
import PopoverButton from '../../common/controls/popoverButton';
import Label from './label';
import FillFromPreviousButtonHeader from './fillFromPreviousButtonHeader';
import { getContent, getFormat, getCurrentJobID } from '../selectors/simpleSelectors';
import { newPathSelector } from '../selectors';
import logValueOnChange from '../commonActions/logValueOnChange';
const safeGetValue = (path, content) => {
  if (path === '/') {
    return content;
  }
  return getValue(path, content);
};
const sourcesSelector = createSelector([getFormat], format => format && format.sources);
const activeContentAtPathSelector = createSelector([getContent, newPathSelector], (content, path) => safeGetValue(path, content));
export const fetchReportTypes = (reportTypeActions, sharedReportTypes) => {
  reportTypeActions.serverIndex(sharedReportTypes.indexQuery);
};
export const fillContentEffect = (addToCollection, activeContentAtPath, duplicateContentPaths, fillTarget, onChange, onToggle, path, reportsLoading, reportShowCancel, selectedSource, selected, sources) => {
  if (selected && !reportsLoading && Object.keys(fillTarget).length > 0) {
    // prepare the selected report's data for copying to current report
    // copy over the selected report data
    let fillContent = getValueAtPath(fillTarget, 'content', null);
    // Need to grab and account for static content in the target report
    const fillStaticContent = getValueAtPath(fillTarget, 'ReportType.staticContent', null);
    if (fillStaticContent) {
      fillContent = mergeDeepLeft(fillContent, JSON.parse(fillStaticContent));
    }
    const fillID = getValueAtPath(fillTarget, 'id', null);
    if (fillContent && selected === fillID) {
      let value = safeGetValue(path, fillContent);
      const previousValue = path === '/' ? {} : activeContentAtPath;
      // derive new value for filling specially if format has "sources"
      const selectedSourceID = getValueAtPath(selectedSource, 'id', null);
      if (addToCollection) {
        // value is an object with UUIDs for keys. keep the values but generate new UUID keys
        const values = Object.values(value);
        value = {};
        const now = moment();
        values.sort(pathComparator('_created')).forEach((val, index) => {
          let newValue = {};
          if (duplicateContentPaths.length > 0) {
            duplicateContentPaths.forEach(dupPath => {
              enumerateWildcardPath(val, dupPath, (cp, c) => {
                newValue = setValue(cp, newValue, c);
              });
            });
          } else {
            newValue = val;
          }
          // eslint-disable-next-line no-underscore-dangle
          newValue._created = now.add(index * 10, 'ms').utc().toISOString(); // set a consistent created date
          value[uuidV4().toUpperCase()] = newValue;
        });
      } else if (selectedSourceID && sources) {
        value = {};
        // match selectedSource to sources defined in report.format
        const source = sources.find(s => s.identifier === selectedSourceID);
        const mapKeys = Object.keys(source.map);
        mapKeys.forEach(pathTarget => {
          const pathFill = source.map[pathTarget];
          value = setValue(pathTarget, value, getValue(pathFill, fillContent));
        });
      }
      let changeValue = _objectSpread(_objectSpread({}, previousValue || {}), value || {});
      // Extra bit of logic to stop us from accidently overwriting content at '/'
      let changePath = path;
      if (changePath === '/') {
        const [key] = Object.keys(changeValue);
        changePath = appendPath(changePath, key);
        changeValue = changeValue[key];
      }

      // Generate a diff to the previous value in content to ensure everything is properly nulled
      const mergePatch = generateMergePatch(getValue(changePath, activeContentAtPath), changeValue) || {};
      onChange({
        path: changePath,
        value: mergeDeepLeft(changeValue, mergePatch)
      });
      reportShowCancel();
    }

    // cleanup
    onToggle();
  }
};
const FillFromPreviousButton = ({
  activeContentAtPath: _activeContentAtPath = {},
  activeID,
  addToCollection: _addToCollection = false,
  currentJobID: _currentJobID = null,
  duplicateContentPaths: _duplicateContentPaths = [],
  enabled: _enabled = true,
  fillReportSelectEditorActions: {
    indexCancel: indexCancelReport,
    serverFilter: serverFilterReports,
    serverShow: serverShowReport,
    showCancel: reportShowCancel
  },
  fillReportSelectJobActions: {
    indexCancel: indexCancelJob,
    serverFilter: serverFilterJobs
  },
  fillTarget: _fillTarget = {},
  filterByCurrentJob: _filterByCurrentJob = false,
  jobIndexQuery,
  jobsLoading,
  jobs,
  label: _label = null,
  onChange,
  path,
  reportsLoading,
  reportIndexQuery,
  reports,
  reportType: sharedReportTypes,
  reportTypeActions,
  reportTypeFillFromActions: {
    indexCancel: indexCancelReportType,
    serverFilter: serverFilterReportTypes
  },
  reportTypeID,
  reportTypeIndexQuery,
  reportTypes,
  reportTypesLoading,
  sources: _sources = null,
  testingIdentifier: _testingIdentifier = '',
  title
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  useEffect(() => {
    fetchReportTypes(reportTypeActions, sharedReportTypes);
  }, [reportTypeActions, sharedReportTypes]);

  // the list will be jobs, reportTypes, or reports
  const list = useMemo(() => {
    let result = [];
    if (!selectedJob) {
      // jobs
      if (!jobsLoading) {
        result = jobs.map(job => ({
          id: job.id,
          isJob: true,
          name: job.jobNumber
        }));
      }
    } else if (_sources && !selectedSource) {
      // reportTypes
      if (!reportTypesLoading) {
        reportTypes.forEach(rt => {
          const source = _sources.find(s => s.identifier === rt.identifier);
          if (source) {
            const pathFill = [];
            const mapKeys = Object.keys(source.map);
            mapKeys.forEach(pathTarget => {
              pathFill.push(source.map[pathTarget]);
            });
            result.push({
              id: rt.identifier,
              isSource: true,
              name: source.title,
              showItemIfValue: source.showItemIfValue,
              pathFill // where the content is coming from
            });
          }
        });
      }
    } else {
      // reports
      result = reports.map(_ref => {
        let {
            reportNumber,
            reportTypeName
          } = _ref,
          rest = _objectWithoutProperties(_ref, _excluded);
        return _objectSpread(_objectSpread({}, rest), {}, {
          name: `${reportTypeName} ${reportNumber}`
        });
      });
    }
    return result;
  }, [jobs, jobsLoading, reports, reportTypes, reportTypesLoading, selectedJob, selectedSource, _sources]);
  const paths = useMemo(() => {
    const result = [];
    if (_sources) {
      _sources.forEach(source => {
        const mapKeys = Object.keys(source.map);
        mapKeys.forEach(pathTarget => {
          result.push(source.map[pathTarget]);
        });
      });
    } else {
      result.push(path);
    }
    return result;
  }, [path, _sources]);
  const onSelect = useCallback(item => {
    const {
      id,
      isJob,
      isSource,
      pathFill = [],
      showItemIfValue
    } = item;
    const reportContentPaths = paths.concat(pathFill);
    if (isJob) {
      setSelectedJob(id);
      if (!_sources) {
        // fetch reports
        const onlyOnDemand = false; // always re-fetch for up-to-date lists
        serverFilterReports({
          contentPath: reportContentPaths,
          reportType: [reportTypeID],
          // only filter on the same reportType
          showItemIfValue,
          job: [id]
        }, reportIndexQuery, onlyOnDemand);
      } else {
        // fetch reportTypes
        const onlyOnDemand = false; // always re-fetch for up-to-date lists
        serverFilterReportTypes({
          reportContentPath: paths,
          job: [id]
        }, reportTypeIndexQuery, onlyOnDemand);
      }
    } else if (isSource) {
      const onlyOnDemand = false; // always re-fetch for up-to-date lists
      serverFilterReports({
        contentPath: reportContentPaths,
        showItemIfValue,
        job: [selectedJob]
      }, reportIndexQuery, onlyOnDemand);
      setSelectedSource(item);
    } else {
      serverShowReport(id);
      setSelected(id);
    }
  }, [paths, selectedJob, serverFilterReports, serverFilterReportTypes, serverShowReport, reportIndexQuery, reportTypeID, reportTypeIndexQuery, _sources]);
  const onToggle = useCallback(() => {
    if (!isOpen) {
      const reportTypeIDs = [];
      if (_sources) {
        const sourceReportTypes = _sources.map(source => source.identifier);
        // filter list for relevant reportTypeIDs and fetch jobs
        sharedReportTypes.list.filter(rt => sourceReportTypes.includes(rt.identifier)).forEach(rt => reportTypeIDs.push(rt.id));
      } else {
        // if no format.sources are defined, filter with the current report's reportType
        reportTypeIDs.push(reportTypeID);
      }
      const filters = {
        closed: false,
        reportsToExclude: [activeID],
        reportContentPath: paths,
        reportType: reportTypeIDs
      };
      if (!_filterByCurrentJob) {
        const onlyOnDemand = false; // always fetch new list of jobs
        serverFilterJobs(filters, jobIndexQuery, onlyOnDemand);
      } else {
        // Don't try to query the server, we already have the current
        // job ID
        onSelect({
          id: _currentJobID,
          isJob: true
        });
      }
    } else {
      // clean up state
      indexCancelJob();
      indexCancelReportType();
      indexCancelReport();
      setSelectedJob(null);
      setSelectedSource(null);
      setSelected(null);
    }
    setIsOpen(!isOpen); // controls UI state
  }, [activeID, _sources, indexCancelJob, indexCancelReport, indexCancelReportType, isOpen, jobIndexQuery, paths, reportTypeID, serverFilterJobs, sharedReportTypes.list, _filterByCurrentJob, onSelect, _currentJobID]);

  // handle a selected report
  useEffect(() => {
    fillContentEffect(_addToCollection, _activeContentAtPath, _duplicateContentPaths, _fillTarget, onChange, onToggle, path, reportsLoading, reportShowCancel, selectedSource, selected, _sources);
  }, [_addToCollection, _activeContentAtPath, _duplicateContentPaths, _fillTarget, onChange, onToggle, path, reportsLoading, reportShowCancel, selectedSource, selected, _sources]);
  const loaded = !jobsLoading && !reportTypesLoading && !reportsLoading;
  return /*#__PURE__*/React.createElement("div", null, _label !== null ? /*#__PURE__*/React.createElement(Label, {
    value: _label
  }) : undefined, /*#__PURE__*/React.createElement(PopoverButton, {
    enabled: _enabled,
    header: /*#__PURE__*/React.createElement(FillFromPreviousButtonHeader, {
      jobIndexQuery: jobIndexQuery,
      jobServerFilter: serverFilterJobs,
      showBack: !_filterByCurrentJob,
      selectedJob: selectedJob,
      selectedSource: selectedSource,
      setSelectedJob: setSelectedJob,
      setSelectedSource: setSelectedSource,
      sources: _sources
    }),
    isOpen: isOpen,
    list: list,
    onSelect: onSelect,
    onToggle: onToggle,
    title: title,
    testingIdentifier: _testingIdentifier,
    className: "btn-block tqc-control",
    loaded: loaded
  }));
};
export { FillFromPreviousButton as UncomposedFillFromPreviousButton };
FillFromPreviousButton.propTypes = {
  activeContentAtPath: PropTypes.shape({}),
  activeID: PropTypes.string.isRequired,
  addToCollection: PropTypes.bool,
  duplicateContentPaths: PropTypes.arrayOf(PropTypes.string),
  enabled: PropTypes.bool,
  currentJobID: PropTypes.string,
  filterByCurrentJob: PropTypes.bool,
  fillReportSelectEditorActions: PropTypes.shape({
    indexCancel: PropTypes.func,
    serverFilter: PropTypes.func,
    serverShow: PropTypes.func,
    showCancel: PropTypes.func
  }).isRequired,
  fillReportSelectJobActions: PropTypes.shape({
    indexCancel: PropTypes.func,
    serverFilter: PropTypes.func
  }).isRequired,
  fillTarget: PropTypes.shape({
    id: PropTypes.string
  }),
  jobIndexQuery: PropTypes.shape({}).isRequired,
  jobsLoading: PropTypes.bool.isRequired,
  jobs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  label: PropTypes.string,
  reportsLoading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  reportIndexQuery: PropTypes.shape({}).isRequired,
  reports: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  reportTypeActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  reportType: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  reportTypeFillFromActions: PropTypes.shape({
    indexCancel: PropTypes.func,
    serverFilter: PropTypes.func
  }).isRequired,
  reportTypeID: PropTypes.string.isRequired,
  reportTypeIndexQuery: PropTypes.shape({}).isRequired,
  reportTypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  reportTypesLoading: PropTypes.bool.isRequired,
  sources: PropTypes.arrayOf(PropTypes.shape({})),
  testingIdentifier: PropTypes.string,
  title: PropTypes.string.isRequired
};
export default entityComposition(FillFromPreviousButton, ['reportType'], {
  currentJobID: getCurrentJobID,
  activeContentAtPath: activeContentAtPathSelector,
  activeID: state => state.report.active.id,
  fillTarget: state => state.fillReportSelectEditor.active,
  jobIndexQuery: state => state.fillReportSelectJob.indexQuery,
  jobsLoading: state => state.fillReportSelectJob.loading,
  jobs: state => state.fillReportSelectJob.list,
  reportsLoading: state => state.fillReportSelectEditor.loading,
  reportIndexQuery: state => state.fillReportSelectEditor.indexQuery,
  // Kick the current report out of the potential list of reports
  reports: state => state.fillReportSelectEditor.list.filter(({
    id
  }) => id !== state.report.active.id),
  reportTypeID: state => state.report.active.reportTypeID,
  reportTypeIndexQuery: state => state.reportTypeFillFrom.indexQuery,
  reportTypes: state => state.reportTypeFillFrom.list,
  reportTypesLoading: state => state.reportTypeFillFrom.loading,
  sources: sourcesSelector,
  path: newPathSelector,
  onChange: logValueOnChange
}, ['fillReportSelectEditor', 'fillReportSelectJob', 'reportTypeFillFrom']);